import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import {
  findImageByKey,
  findImageByKeyWebLarge,
  findValueByKey,
  formatDateLang,
  getResourceContentImageSrc,
  getTarget,
  isItUndefined,
  isLinkAbsolute,
  noTruncationForAllScreen,
  removeNbsp,
  stripHtml,
} from '../../util/DataUtil';
import ArticleThumbnailCard from '../shared/ArticleThumbnailCard';

const TempalteFeaturedContent = ({ children, global }) => {
  const featured = children?.field_featured_posts;
  const [titles, setTitles] = useState([]);
  const title = isItUndefined(children?.field_title_color);
  const router = useRouter();
  const isBlogVariant =
    children?.field_featured_content_variant?.[0]?.value ===
    'blog_featured_content';
  const { asPath } = router;
  const getFullTitle = (item) => {
    const titleFromTemplate = findValueByKey(
      'template_title_block_type',
      item?.field_template_blocks
    )?.field_template_title?.[0]?.value;

    if (titleFromTemplate) return titleFromTemplate;

    const heroTitle = findValueByKey(
      'hero_graphic_block_type',
      item?.field_template_blocks
    )?.field_formatted_title?.[0]?.value;

    return removeNbsp(stripHtml(heroTitle));
  };

  const truncate = () => {
    if (!featured?.length) {
      setTitles([]);
      return;
    }

    if (isBlogVariant) {
      setTitles(featured.map((item) => getFullTitle(item)));
      return;
    }

    const next = featured.map((item, index) =>
      noTruncationForAllScreen(
        getFullTitle(item),
        index === 0 ? 120 : 47,
        50,
        60
      )
    );

    setTitles(next);
  };

  useEffect(() => {
    truncate();
  }, [featured, isBlogVariant]);

  const getContentTypeLabel = (item) => {
    if (item?.field_facility_location?.length > 0) {
      return global?.field_facility_label?.[0]?.value || 'Facility';
    }

    if (item?.field_metro_name?.length > 0) {
      return global?.field_metro_label?.[0]?.value || 'Metro';
    }

    if (item?.field_region_name?.length > 0) {
      return global?.field_region_label?.[0]?.value || 'Region';
    }

    return (
      item?.field_content_type_templat?.[0]?.name ||
      // item?.field_page_type?.[0]?.value ||
      null
    );
  };

  const onChipClick = (event, category) => {
    if (typeof window !== 'undefined') {
      // localStorage.setItem('name', category[0]['name'])
      let url = '';
      if (typeof window !== undefined) {
        url = window.location.pathname;
      }

      if (isBlogVariant) {
        router.push({
          pathname: '/resources/blog',
          query: { contentTag: category[0].name.toLowerCase() },
          hash: 'articles-cards-container',
        });
      } else
        router.push({
          pathname: '/resources',
          query: { category: category[0]['name'].toLowerCase() },
        });
    }
  };

  const renderBlogInfo = (blogItem) => {
    // const authors = blogItem?.field_authors || [];

    // const authorNames =
    //   authors
    //     ?.map((a) => a?.field_author_full_name?.[0]?.value)
    //     .filter(Boolean)
    //     .join(', ') || null;

    const authorBlocks =
      blogItem?.field_template_blocks?.filter(
        (block) => block.author_block !== undefined
      ) || [];

    const authorNames =
      authorBlocks
        ?.map((a) => a?.author_block?.field_author_name?.[0]?.value)
        .filter(Boolean)
        .join(', ') || null;

    const publishDate = isItUndefined(blogItem?.field_publish_date) || null;
    const timeToRead = isItUndefined(blogItem?.field_time_to_read) || null;

    // Nothing to show → render nothing, so the shared card doesn't add an empty
    // subheader row (matches renderResourceMeta's behaviour).
    // if (!authorNames && !publishDate && !timeToRead) {
    //   return null;
    // }

    return (
      <div className={'blog-info'}>
        <span className={'author-name info-item'}>
          {authorNames ? `${authorNames}` : ''}
        </span>
        {authorNames && publishDate ? (
          <span className={'separator separator-inline info-item'}>
            {' | '}
          </span>
        ) : null}
        {/*{authors.length > 1 && !isFeaturedItem && <br />}*/}
        <span className={'publish-date info-item'}>
          {publishDate ? formatDateLang(publishDate) : ''}
        </span>
        {(authorNames || publishDate) && timeToRead ? (
          <span className={'separator'}>{' | '}</span>
        ) : null}
        <span className={'blog-read-time info-item'}>
          {timeToRead ? timeToRead : ''}
        </span>
      </div>
    );
  };

  // Resources reuse Blog's Author|Date line, but with the Content Type in the
  // author's place: "Content Type | Date". If the asset has no date, drop the
  // "| Date" part and show just the Content Type. Uses the same blog-info
  // classes so it matches Blog styling.
  const renderResourceMeta = (item) => {
    const label = getContentTypeLabel(item);
    if (!label) return null;
    const publishDate = isItUndefined(item?.field_publish_date) || null;
    return (
      <div className={'blog-info'}>
        <span className={'info-item'}>{label}</span>
        {publishDate ? (
          <>
            <span className={'separator separator-inline info-item'}>
              {' | '}
            </span>
            <span className={'publish-date info-item'}>
              {formatDateLang(publishDate)}
            </span>
          </>
        ) : null}
      </div>
    );
  };

  // Adapt the shared card's onTagClick(event, tag) to the existing chip router
  // (used by the Resources variant cards).
  const handleResourceTagClick = (event, tag) => {
    onChipClick(event, [{ name: tag?.name }]);
  };

  // Blog variant: the primary category routes by its source translation, the
  // secondary categories by name (matches the old blog-pill behaviour). Routing
  // to /resources/blog is handled by onChipClick via isBlogVariant.
  const handleBlogTagClick = (event, tag) => {
    onChipClick(event, [{ name: tag?.field_source_translation || tag?.name }]);
  };

  // Hero thumbnail — both Blog and Resources render from the shared card so they
  // are identical by construction. `hero` gives the larger Blog Featured hero
  // typography/spacing (defined once in the shared card). The block owns only
  // layout (hero width, image height) via SCSS. Blog adds the excerpt via
  // `description` and its Author|Date|ReadTime meta; Resources uses Content
  // Type|Date. Chips come from primary + secondary categories in both.
  const renderMainFeatured = () => {
    const item = featured?.[0];
    if (!item) return null;
    return (
      <div className={`item-big ${isBlogVariant ? 'blog' : ''}`}>
        <ArticleThumbnailCard
          hero
          imagePriority
          href={item?.['url-alias']?.length ? item['url-alias'] : '/'}
          target={getTarget(item?.['url-alias'])}
          image={getResourceContentImageSrc(item)}
          imageAlt="featured"
          header={
            isItUndefined(item?.field_display_title)
              ? isItUndefined(item?.field_display_title)
              : titles[0] || item?.title
          }
          description={
            isBlogVariant
              ? stripHtml(isItUndefined(item?.field_slug))
              : undefined
          }
          subheader={
            isBlogVariant ? renderBlogInfo(item) : renderResourceMeta(item)
          }
          primaryCategory={item?.field_template_category?.[0]}
          secondaryCategories={item?.field_template_secondary_categor}
          onTagClick={
            isBlogVariant ? handleBlogTagClick : handleResourceTagClick
          }
        />
        <hr className="item-big-hr" />
      </div>
    );
  };

  // Small items — both variants render from the shared card. Image visibility is
  // controlled in SCSS: on mobile only the hero shows an image (small items are
  // text-only); on desktop the first small item shows its image (magazine layout
  // beside the hero) while the rest stay text-only via the `item--text-desktop`
  // class. Blog uses Author|Date|ReadTime meta and blog tag routing; Resources
  // uses Content Type|Date.
  const renderFeatured = () => {
    return featured?.map((item, index) => {
      if (index === 0) return null;

      const href = item?.['url-alias']?.length ? item['url-alias'] : '/';
      const target = getTarget(item?.['url-alias']);

      return (
        <div
          key={`featured-${item?.['url-alias'] || item?.nid || index}`}
          className={`item${index > 1 ? ' item--text-desktop' : ''}${isBlogVariant ? ' blog' : ''}`}
        >
          <ArticleThumbnailCard
            imagePriority={index === 1}
            href={href}
            target={target}
            image={getResourceContentImageSrc(item)}
            imageAlt="featured"
            header={
              isItUndefined(item?.field_display_title)
                ? isItUndefined(item?.field_display_title)
                : titles[index] || item?.title
            }
            subheader={
              isBlogVariant ? renderBlogInfo(item) : renderResourceMeta(item)
            }
            primaryCategory={item?.field_template_category?.[0]}
            secondaryCategories={item?.field_template_secondary_categor}
            onTagClick={
              isBlogVariant ? handleBlogTagClick : handleResourceTagClick
            }
          />
          <hr />
        </div>
      );
    });
  };

  return (
    <div className={`template-featured-content ${isBlogVariant ? 'blog' : ''}`}>
      <div className="inner-container">
        {isBlogVariant ? null : (
          <>
            <div
              className="page-sub"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <hr />
          </>
        )}
        <div className={'featured'}>
          {renderMainFeatured()}
          <div className={`small-items ${isBlogVariant ? 'blog' : ''}`}>
            {renderFeatured()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempalteFeaturedContent;
